// Esqueletos de carga (placeholders con la forma de lo que viene).
//
// Por qué existen: la espera del dashboard era un spinner solo en el centro
// de la pantalla; si el sistema operativo pide movimiento reducido, ese
// círculo quedaba quieto y parecía una pantalla colgada. Un esqueleto da
// contexto (qué se está cargando y dónde va a aparecer) y pulsa como
// movimiento esencial (ver `.motion-essential` en styles/tokens.css).

import { cn } from '../../lib/utils';

export function Skeleton({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'block animate-pulse rounded-md bg-surface-overlay motion-essential',
        className,
      )}
    />
  );
}

interface SkeletonDashboardProps {
  /** Filas de la tabla de líneas a dibujar. */
  filas?: number;
  /** Texto accesible anunciado una sola vez (role=status). */
  etiqueta?: string;
  className?: string;
}

/**
 * Esqueleto de un dashboard: saludo, 3 KPI y una tabla. Un solo `role=status`
 * para lectores de pantalla; las piezas internas son decorativas.
 */
export function SkeletonDashboard({
  filas = 6,
  etiqueta = 'Cargando dashboard',
  className,
}: SkeletonDashboardProps) {
  return (
    <div
      role="status"
      aria-label={etiqueta}
      aria-busy="true"
      className={cn('space-y-6', className)}
    >
      <div className="space-y-2">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-80" />
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {Array.from({ length: 3 }, (_, i) => (
          <div
            key={i}
            data-testid="skeleton-kpi"
            className="space-y-3 rounded-lg border border-border bg-surface-elevated p-4"
          >
            <Skeleton className="h-3 w-28" />
            <Skeleton className="h-9 w-32" />
            <Skeleton className="h-3 w-40" />
          </div>
        ))}
      </div>
      <div className="space-y-3 rounded-lg border border-border bg-surface-elevated p-4">
        <Skeleton className="h-3 w-36" />
        {Array.from({ length: filas }, (_, i) => (
          <div key={i} data-testid="skeleton-fila" className="flex items-center gap-4">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="ml-auto h-2 w-24" />
          </div>
        ))}
      </div>
      <span className="sr-only">{etiqueta}…</span>
    </div>
  );
}
